import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  Tooltip,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { ResourceGroupSelect } from '@/components/selectors/resourceGroupSelect';
import { RestoreRegionSelect } from '@/components/selectors/restoreRegionSelect';
import { Icon } from '@/components/shared/icon';
import { TransactionalTextField } from '@/components/shared/transactionalTextField';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreAzureSqlDBState } from '../restoreAzureSqlDbWizard';

export const AzureSqlDbConfiguration = (
  props: StepProps<RestoreAzureSqlDBState>
) => {
  const dal = useDAL();
  const { stepperLabels, back } = props;
  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);

  const setServerName = (serverName?: string) =>
    props.setWizardState((state) => ({ ...state, serverName }));
  const setAdminLogin = (adminLogin?: string) =>
    props.setWizardState((state) => ({ ...state, adminLogin }));
  const onKeepOriginalTagsChange = (keepOriginalTags: boolean) =>
    props.setWizardState((state) => ({ ...state, keepOriginalTags }));
  const onTagsChange = (tags: Record<string, string>) =>
    props.setWizardState((state) => ({ ...state, tags }));

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);
    const tags = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon:restore': 'true',
    };
    return dal.restore.azureMssql
      .restore(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          azureSqlDatabase: {
            region: props.wizardState.location!,
            resourceGroupName: props.wizardState.resourceGroup!,
            serverName: props.wizardState.serverName ?? '',
            adminUserName: props.wizardState.adminLogin ?? '',
            tags,
          },
        },
      })
      .then(() =>
        props.finish(`Started restore job for ${props.wizardState.serverName}`)
      )
      .catch(async (e) => {
        setIsRestoring(false);
        const errorMessage = await extractErrorMessage(e);
        setError(`Failed to restore DB: ${errorMessage}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={stepperLabels}
      stepperIndex={stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={back}
      canGoNext={() =>
        !isRestoring &&
        props.wizardState.serverName !== '' &&
        props.wizardState.adminLogin !== '' &&
        props.wizardState.resourceGroup !== '' &&
        props.wizardState.location !== '' &&
        props.wizardState.resourceGroup !== undefined
      }
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <FormControl size='small' className='flex-1'>
            <FormLabel>Sql Server Name</FormLabel>
            <TransactionalTextField
              initValue={props.wizardState.serverName ?? ''}
              onChange={setServerName}
            />
          </FormControl>
          <RestoreRegionSelect
            restoreAccountId={props.wizardState.restoreAccount?.id}
            cloudProvider={Provider.Azure}
            regionName={props.wizardState.location}
            onChange={(location) =>
              props.setWizardState((state) => ({ ...state, location }))
            }
          />
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <ResourceGroupSelect
            accountId={props.wizardState.restoreAccount?.id}
            resourceGroup={props.wizardState.resourceGroup}
            onChange={(resourceGroup) =>
              props.setWizardState((state) => ({
                ...state,
                resourceGroup,
              }))
            }
          />
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <FormControl size='small' className='flex-1'>
            <FormLabel>
              <Stack alignItems={'center'} direction={'row'}>
                Admin Login*
                <Tooltip
                  slotProps={{
                    tooltip: {
                      sx: { padding: '16px' },
                    },
                  }}
                  title={`A random password will be generated and can be reset after the restore is complete.`}
                >
                  <Icon iconClass='material-symbols-help-outline-rounded' />
                </Tooltip>
              </Stack>
            </FormLabel>
            <TransactionalTextField
              initValue={props.wizardState.adminLogin ?? ''}
              onChange={setAdminLogin}
            />
          </FormControl>
        </Stack>
      </Box>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={onKeepOriginalTagsChange}
          onChange={onTagsChange}
        />
      </CardContent>
    </StepContainer>
  );
};
